import { useState, useMemo } from 'react'
import { useSessions } from '../hooks/useSessions'
import { formatEur, formatKwh, formatSntKwh, formatDateTimeLocal } from '../utils/formatters'
import { useTranslation } from 'react-i18next'
import { subDays, startOfMonth, subMonths, startOfQuarter, subQuarters, parseISO, format } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'

const TZ = 'Europe/Helsinki'

interface Session {
  startTime: string
  endTime: string
  totalEnergyKwh: number
  totalCostEur: number
  costCalculated: boolean
  priceEstimated: boolean
  chargerName: string
  zaptecSessionId: string
  intervals?: string
  rawData?: string
}

interface Interval {
  startTimeUtc: string
  endTimeUtc: string
  energyKwh: number
  spotPriceSntKwh: number
  marginSntKwh: number
  intervalCostEur: number
}

function parseIntervals(json?: string): Interval[] {
  if (!json) return []
  try { return JSON.parse(json) } catch { return [] }
}

function downloadFile(content: string, filename: string, type: string) {
  const blob = new Blob(['\uFEFF' + content], { type: `${type};charset=utf-8` })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

function localTime(utcIso: string): string {
  return format(toZonedTime(parseISO(utcIso), TZ), 'dd.MM.yyyy HH:mm')
}

function exportCsv(sessions: Session[], t: (k: string) => string) {
  const sep = ';'
  const header = [
    t('sessions.startTime'), t('sessions.endTime'), t('sessions.charger'),
    t('sessions.time'), t('sessions.kwh'), t('sessions.spot') + ' (snt/kWh)',
    t('sessions.margin') + ' (snt/kWh)', t('sessions.price') + ' (EUR)',
  ].join(sep)

  const rows: string[] = [header]
  for (const s of sessions) {
    const intervals = parseIntervals(s.intervals)
    if (intervals.length === 0) {
      rows.push([
        localTime(s.startTime), localTime(s.endTime), s.chargerName,
        '', s.totalEnergyKwh.toFixed(4), '', '', s.totalCostEur.toFixed(4),
      ].join(sep))
    } else {
      for (const iv of intervals) {
        rows.push([
          localTime(s.startTime), localTime(s.endTime), s.chargerName,
          localTime(iv.startTimeUtc), iv.energyKwh.toFixed(4),
          iv.spotPriceSntKwh.toFixed(4), iv.marginSntKwh.toFixed(4),
          iv.intervalCostEur.toFixed(4),
        ].join(sep))
      }
    }
  }
  downloadFile(rows.join('\n'), `lataukset_${new Date().toISOString().slice(0, 10)}.csv`, 'text/csv')
}

function exportPdf(sessions: Session[], totalKwh: number, totalEur: number, t: (k: string) => string) {
  const rows = sessions.map(s => {
    const ivs = parseIntervals(s.intervals)
    const spotEur = ivs.reduce((sum, iv) => sum + iv.energyKwh * iv.spotPriceSntKwh / 100, 0)
    const marginEur = ivs.reduce((sum, iv) => sum + iv.energyKwh * iv.marginSntKwh / 100, 0)
    return `<tr>
      <td>${localTime(s.startTime)}</td>
      <td>${s.chargerName}</td>
      <td style="text-align:right">${s.totalEnergyKwh.toFixed(2)} kWh</td>
      <td style="text-align:right">${spotEur.toFixed(4)} €</td>
      <td style="text-align:right">${marginEur.toFixed(4)} €</td>
      <td style="text-align:right"><strong>${s.totalCostEur.toFixed(4)} €</strong></td>
    </tr>`
  }).join('')

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
    <title>${t('sessions.exportTitle')}</title>
    <style>
      body { font-family: Arial, sans-serif; font-size: 12px; margin: 20px; }
      h1 { font-size: 18px; }
      table { width: 100%; border-collapse: collapse; margin-top: 12px; }
      th, td { border: 1px solid #ccc; padding: 4px 8px; }
      th { background: #f0f0f0; text-align: left; }
      .summary { margin: 12px 0; }
      .summary span { margin-right: 20px; }
      @media print { button { display: none; } }
    </style>
  </head><body>
    <h1>${t('sessions.exportTitle')} — Kilterinlinna</h1>
    <div class="summary">
      <span><strong>${sessions.length}</strong> ${t('sessions.sessionCount')}</span>
      <span><strong>${totalKwh.toFixed(2)} kWh</strong></span>
      <span><strong>${totalEur.toFixed(4)} €</strong></span>
    </div>
    <table>
      <thead><tr>
        <th>${t('sessions.time')}</th>
        <th>${t('sessions.charger')}</th>
        <th style="text-align:right">${t('sessions.kwh')}</th>
        <th style="text-align:right">${t('sessions.spot')}</th>
        <th style="text-align:right">${t('sessions.margin')}</th>
        <th style="text-align:right">${t('sessions.totalCost')}</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <button onclick="window.print()" style="margin-top:16px;padding:8px 16px;cursor:pointer">
      ${t('sessions.print')}
    </button>
  </body></html>`

  const w = window.open('', '_blank')
  if (w) { w.document.write(html); w.document.close() }
}

function parseTokenName(rawData?: string): string {
  if (!rawData) return ''
  try { return JSON.parse(rawData).TokenName ?? '' } catch { return '' }
}

function toLocalDateStr(d: Date): string {
  return format(d, 'yyyy-MM-dd')
}

function sessionEndDateLocal(endTime: string): string {
  return format(toZonedTime(parseISO(endTime), TZ), 'yyyy-MM-dd')
}

export function SessionsPage() {
  const { t } = useTranslation()
  const { sessions, loading, error } = useSessions()
  const [expandedId, setExpandedId] = useState<string | null>(null)

  // Default: last 30 days
  const [fromDate, setFromDate] = useState(toLocalDateStr(subDays(new Date(), 30)))
  const [toDate, setToDate] = useState(toLocalDateStr(new Date()))

  const typed = sessions as unknown as Session[]

  const filtered = useMemo(() => {
    const sorted = [...typed].sort((a, b) => (b.endTime || b.startTime).localeCompare(a.endTime || a.startTime))
    if (!fromDate && !toDate) return sorted
    return sorted.filter(s => {
      // Filter by endTime (billing date) in local timezone
      const d = sessionEndDateLocal(s.endTime || s.startTime)
      if (fromDate && d < fromDate) return false
      if (toDate && d > toDate) return false
      return true
    })
  }, [typed, fromDate, toDate])

  // Summary
  const totalKwh = filtered.reduce((s, x) => s + x.totalEnergyKwh, 0)
  const totalEur = filtered.reduce((s, x) => s + x.totalCostEur, 0)

  const now = new Date()
  const endOfPrevMonth = subDays(startOfMonth(now), 1)
  const presets = [
    { label: t('sessions.presetCurMonth'), fn: () => { setFromDate(toLocalDateStr(startOfMonth(now))); setToDate(toLocalDateStr(now)) } },
    { label: t('sessions.presetPrevMonth'), fn: () => { setFromDate(toLocalDateStr(startOfMonth(subMonths(now, 1)))); setToDate(toLocalDateStr(endOfPrevMonth)) } },
    { label: t('sessions.presetCurQuarter'), fn: () => { setFromDate(toLocalDateStr(startOfQuarter(now))); setToDate(toLocalDateStr(now)) } },
    { label: t('sessions.presetPrevQuarter'), fn: () => { const pq = subQuarters(now, 1); setFromDate(toLocalDateStr(startOfQuarter(pq))); setToDate(toLocalDateStr(subDays(startOfQuarter(now), 1))) } },
    { label: t('sessions.presetLast30'), fn: () => { setFromDate(toLocalDateStr(subDays(now, 30))); setToDate(toLocalDateStr(now)) } },
    { label: t('sessions.presetAll'), fn: () => { setFromDate(''); setToDate('') } },
  ]

  if (loading) return <p className="text-brand-500 py-8 text-center">{t('sessions.loading')}</p>
  if (error) return <p className="text-red-500 py-8 text-center">{error}</p>

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-brand-700">{t('nav.sessions')}</h1>

      {/* Date filter */}
      <div className="space-y-2">
        <div className="flex flex-wrap gap-1">
          {presets.map(p => (
            <button key={p.label} onClick={p.fn}
              className="text-xs px-3 py-1 rounded-lg border border-brand-100 text-brand-600 hover:bg-brand-50 transition-colors">
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
            className="rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500" />
          <span className="text-brand-400">—</span>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
            className="rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500" />
        </div>
      </div>

      {/* Summary + export */}
      <div className="flex items-center gap-4 text-sm">
        <span className="text-brand-600">{filtered.length} {t('sessions.sessionCount')}</span>
        <span className="text-brand-600">{formatKwh(totalKwh)}</span>
        <span className="font-semibold text-brand-700">{formatEur(totalEur)}</span>
        <div className="ml-auto flex gap-2">
          <button onClick={() => exportCsv(filtered, t)}
            disabled={filtered.length === 0}
            className="text-xs px-3 py-1.5 rounded-lg border border-brand-200 text-brand-600 hover:bg-brand-50 disabled:opacity-40 transition-colors">
            CSV
          </button>
          <button onClick={() => exportPdf(filtered, totalKwh, totalEur, t)}
            disabled={filtered.length === 0}
            className="text-xs px-3 py-1.5 rounded-lg border border-brand-200 text-brand-600 hover:bg-brand-50 disabled:opacity-40 transition-colors">
            PDF
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="text-brand-400">{t('sessions.noSessions')}</p>
      ) : (
        <div className="space-y-3">
          {filtered.map((s) => {
            const sid = s.zaptecSessionId
            const isOpen = expandedId === sid
            const intervals = parseIntervals(s.intervals)
            const tokenName = isOpen ? parseTokenName(s.rawData) : ''

            const totalSpotEur = intervals.reduce((sum, iv) => sum + iv.energyKwh * iv.spotPriceSntKwh / 100, 0)
            const totalMarginEur = intervals.reduce((sum, iv) => sum + iv.energyKwh * iv.marginSntKwh / 100, 0)

            return (
              <div key={sid} className="bg-white rounded-xl shadow-sm overflow-hidden">
                <button
                  onClick={() => setExpandedId(isOpen ? null : sid)}
                  className="w-full px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 text-left hover:bg-brand-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-brand-700">
                      {formatDateTimeLocal(s.endTime || s.startTime)}
                    </span>
                    <span className="text-xs text-brand-400">{s.chargerName}</span>
                    {s.priceEstimated && (
                      <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">{t('sessions.estimated')}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-brand-600">{formatKwh(s.totalEnergyKwh)}</span>
                    {intervals.length > 0 && (
                      <>
                        <span className="text-brand-400 text-xs">{t('sessions.spotLabel')} {formatEur(totalSpotEur)}</span>
                        <span className="text-brand-400 text-xs">+ {t('sessions.marginLabel')} {formatEur(totalMarginEur)}</span>
                        <span className="text-brand-400">=</span>
                      </>
                    )}
                    <span className="text-brand-700 font-semibold">{formatEur(s.totalCostEur)}</span>
                    <span className="text-brand-400 text-xs">{isOpen ? '▲' : '▼'}</span>
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-brand-50 px-4 py-4 space-y-4">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                      <div>
                        <p className="text-brand-400 text-xs">{t('sessions.startTime')}</p>
                        <p className="font-medium text-brand-700">{formatDateTimeLocal(s.startTime)}</p>
                      </div>
                      <div>
                        <p className="text-brand-400 text-xs">{t('sessions.endTime')}</p>
                        <p className="font-medium text-brand-700">{formatDateTimeLocal(s.endTime)}</p>
                      </div>
                      <div>
                        <p className="text-brand-400 text-xs">{t('sessions.charger')}</p>
                        <p className="font-medium text-brand-700">{s.chargerName}</p>
                      </div>
                      {(
                        <div>
                          <p className="text-brand-400 text-xs">{t('sessions.activatedWith')}</p>
                          <p className="font-medium text-brand-700">{tokenName || t('sessions.zaptecApp')}</p>
                        </div>
                      )}
                    </div>

                    {intervals.length > 0 && (
                      <div className="bg-brand-50 rounded-lg p-3">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                          <div>
                            <p className="text-brand-400 text-xs">{t('sessions.totalEnergy')}</p>
                            <p className="font-bold text-brand-700">{formatKwh(s.totalEnergyKwh)}</p>
                          </div>
                          <div>
                            <p className="text-brand-400 text-xs">{t('sessions.energyCost')}</p>
                            <p className="font-medium text-brand-600">{formatEur(totalSpotEur)}</p>
                          </div>
                          <div>
                            <p className="text-brand-400 text-xs">{t('sessions.marginCost')}</p>
                            <p className="font-medium text-brand-600">{formatEur(totalMarginEur)}</p>
                          </div>
                          <div>
                            <p className="text-brand-400 text-xs">{t('sessions.totalCost')}</p>
                            <p className="font-bold text-brand-700">{formatEur(s.totalCostEur)}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {intervals.length === 0 ? (
                      <p className="text-brand-400 text-sm">{t('sessions.noIntervals')}</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-brand-500 text-xs">
                              <th className="pb-1">{t('sessions.time')}</th>
                              <th className="pb-1 text-right">{t('sessions.kwh')}</th>
                              <th className="pb-1 text-right">{t('sessions.spot')}</th>
                              <th className="pb-1 text-right">{t('sessions.margin')}</th>
                              <th className="pb-1 text-right">{t('sessions.price')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {intervals.map((iv, i) => (
                              <tr key={i} className="border-t border-brand-50">
                                <td className="py-1 text-brand-600">{formatDateTimeLocal(iv.startTimeUtc)}</td>
                                <td className="py-1 text-right">{formatKwh(iv.energyKwh)}</td>
                                <td className="py-1 text-right">{formatSntKwh(iv.spotPriceSntKwh)}</td>
                                <td className="py-1 text-right">{formatSntKwh(iv.marginSntKwh)}</td>
                                <td className="py-1 text-right font-medium">{formatEur(iv.intervalCostEur)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
